import React from 'react';
import PropTypes from 'prop-types';
import { clsx } from 'clsx';
import { TCITooltip } from '@/components/ui/Tooltip';
import styles from './ButtonWithTooltip.module.scss';

function ButtonWithTooltip(props) {
  const additionalClasses = React.Children.count(props.children) > 0 ?
    'tw-border tw-border-light-grey tw-border-solid tw-p-0.5 tw-box-border tw-rounded' : '';

  const handleKeyDown = (e) => {
    if (props.isDisabled) return;
    if (![' ', 'Enter'].includes(e.key)) return;

    e.preventDefault();
    e.currentTarget.click();
  };

  const linkProps = {
    'aria-hidden': props.ariaHidden.toString(),
    'aria-label': props.ariaLabel,
    className: `${props.className} ${props.isDisabled ? styles.disabled : ''} ${styles.iconLink} ${additionalClasses}`,
    'data-confirm': props.dataConfirm,
    'data-method': props.dataMethod,
    onClick: props.onClick,
    onKeyDown: handleKeyDown,
    rel: props.rel,
    tabIndex: 0,
  };

  const hrefProps = props.shouldNavigate ? {
    href: props.isDisabled ? null : props.link,
    target: props.openInNewWindow ? '_blank' : null
  } : { type: 'button' };

  const TagName = props.shouldNavigate ? 'a' : 'button';

  const content = () => {
    if (props.children && React.Children.count(props.children) > 0) {
      return props.children;
    }

    if (props.icon) {
      return (
        <div className="tw-border tw-border-[#CCC] tw-border-solid tw-p-2 tw-rounded tw-mr-1">
          {props.icon}
        </div>
      );
    }

    return (
      <i className={clsx(props.iconClassName, styles.icon, {
        [styles.inverted]: props.inverted,
        [styles.disabled]: props.isDisabled })}
      />
    );
  };

  return (
    <TCITooltip>
      <TCITooltip.Trigger asChild>
        <TagName
          {...linkProps}
          {...hrefProps}
        >
          {content()}
        </TagName>
      </TCITooltip.Trigger>
      <TCITooltip.Content>
        {props.tooltipContent}
      </TCITooltip.Content>
    </TCITooltip>
  );
}

ButtonWithTooltip.propTypes = {
  ariaHidden: PropTypes.bool,
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  dataConfirm: PropTypes.string,
  dataMethod: PropTypes.string,
  icon: PropTypes.element,
  iconClassName: PropTypes.string,
  inverted: PropTypes.bool,
  isDisabled: PropTypes.bool,
  link: PropTypes.string,
  onClick: PropTypes.func,
  openInNewWindow: PropTypes.bool,
  rel: PropTypes.string,
  shouldNavigate: PropTypes.bool,
  tooltipContent: PropTypes.string.isRequired,
  useDivWrapper: PropTypes.bool,
};

ButtonWithTooltip.defaultProps = {
  ariaHidden: false,
  className: '',
  dataConfirm: null,
  dataMethod: null,
  iconClassName: '',
  inverted: false,
  isDisabled: false,
  link: '#',
  onClick: null,
  openInNewWindow: false,
  rel: null,
  shouldNavigate: true,
  useDivWrapper: true,
};

export default ButtonWithTooltip;
